import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import type { OutcomeRowVM } from './derive';
import { formatResolvedPct } from './other-outcomes-format';
import { FinalShareBar, OutcomeThumb } from './other-outcomes-parts';
interface OtherOutcomeCardProps {
  row: OutcomeRowVM;
  rank: number;
  marketPubkey: string;
}
function Stat(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-caption text-foreground-tertiary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span className="font-mono text-body tabular-nums text-foreground-secondary">{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex min-w-0 flex-col gap-0.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Mobile (<md) stacked card for one outcome: rank + thumb + name on top (the
 * winner carries the settled seal), then Final (with bar) / Peak / Volume as inline
 * label:value pairs.
 */
export function OtherOutcomeCard(t0) {
  const $ = _c(53);
  const {
    row,
    rank,
    marketPubkey
  } = t0;
  const t = useTranslations("market.resolvedPanel");
  const {
    formatD6
  } = useFormattedCurrency();
  const t1 = row.isWinner ? "true" : undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex flex-col gap-3 border-b border-border-secondary bg-background-primary px-3 py-3 last:border-b-0");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== rank) {
    t3 = <span className="flex w-5 shrink-0 items-center justify-center font-mono text-body tabular-nums text-foreground-tertiary">{rank}</span>;
    $[1] = rank;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== marketPubkey || $[4] !== row.imageNsfw || $[5] !== row.imageUrl || $[6] !== row.index || $[7] !== row.isWinner || $[8] !== row.label) {
    t4 = <OutcomeThumb marketPubkey={marketPubkey} index={row.index} label={row.label} imageUrl={row.imageUrl} imageNsfw={row.imageNsfw} size="size-8" showSettledBadge={row.isWinner} />;
    $[3] = marketPubkey;
    $[4] = row.imageNsfw;
    $[5] = row.imageUrl;
    $[6] = row.index;
    $[7] = row.isWinner;
    $[8] = row.label;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== row.label) {
    t5 = <span className="flex min-w-0 items-center gap-1.5"><span className="truncate text-body font-medium text-foreground-primary">{row.label}</span></span>;
    $[10] = row.label;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <div className="flex min-w-0 items-center gap-2.5">{t3}{t4}{t5}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t) {
    t7 = t("tableFinal");
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== row.finalSharePct) {
    t8 = <FinalShareBar pct={row.finalSharePct} />;
    $[18] = row.finalSharePct;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== row.finalSharePct) {
    t9 = formatResolvedPct(row.finalSharePct);
    $[20] = row.finalSharePct;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t9) {
    t10 = <span className="w-10 shrink-0 text-right">{t9}</span>;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8) {
    t11 = <span className="flex items-center gap-2">{t8}{t10}</span>;
    $[24] = t10;
    $[25] = t8;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t7) {
    t12 = <Stat label={t7}>{t11}</Stat>;
    $[27] = t11;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t) {
    t13 = t("tablePeak");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== row.peakConsensusPct) {
    t14 = formatResolvedPct(row.peakConsensusPct);
    $[32] = row.peakConsensusPct;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t13 || $[35] !== t14) {
    t15 = <Stat label={t13}>{t14}</Stat>;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t) {
    t16 = t("tableVolume");
    $[37] = t;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = row.volumeTotalD6 ?? "0";
  let t18;
  if ($[39] !== formatD6 || $[40] !== t17) {
    t18 = formatD6(t17, "compact");
    $[39] = formatD6;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t16 || $[43] !== t18) {
    t19 = <Stat label={t16}>{t18}</Stat>;
    $[42] = t16;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t12 || $[46] !== t15 || $[47] !== t19) {
    t20 = <div className="grid grid-cols-3 gap-3 pl-7">{t12}{t15}{t19}</div>;
    $[45] = t12;
    $[46] = t15;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t1 || $[50] !== t20 || $[51] !== t6) {
    t21 = <div data-testid="resolved-panel-outcome-card" data-winner={t1} className={t2}>{t6}{t20}</div>;
    $[49] = t1;
    $[50] = t20;
    $[51] = t6;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  return t21;
}